import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { useLocation } from '@/shared/hooks/useLocation';
import { mediaApi, uploadFile } from '@/modules/media/api/mediaApi';
import { settingsKeys } from '@/modules/settings/queryKeys';
import styles from './AssetsPage.module.css';

function LogoUpload({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: logoAssets } = useQuery({
    queryKey: settingsKeys.locationLogo(locationId),
    queryFn: () => mediaApi.list(locationId, 'logo'),
    enabled: !!locationId,
  });

  const logo = logoAssets?.data?.find((a) => a.status === 'ready') ?? null;

  const removeMutation = useMutation({
    mutationFn: (assetId: string) => mediaApi.remove(locationId, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locationLogo(locationId) });
      toast.success('Logo removed.');
    },
    onError: () => toast.error('Failed to remove logo.'),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadFile({ locationId, file, purpose: 'logo' });
      queryClient.invalidateQueries({ queryKey: settingsKeys.locationLogo(locationId) });
      toast.success('Logo uploaded.');
    } catch {
      toast.error('Upload failed. Use JPG, PNG, or WebP under the size limit.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <Card>
      <div className={styles.assetSection}>
        <div className={styles.assetHeader}>
          <h3 className={styles.assetTitle}>Practice Logo</h3>
          <p className={styles.assetDesc}>
            Used across campaigns, reports, and printed materials. JPG, PNG, or WebP.
          </p>
        </div>
        <div className={styles.logoRow}>
          {logo ? (
            <img src={logo.url} alt="Practice logo" className={styles.logoPreview} />
          ) : (
            <div className={styles.logoPlaceholder}>No logo</div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className={styles.hiddenInput}
            onChange={handleFileChange}
          />
          <div className={styles.logoActions}>
            <Button
              variant="ghost"
              size="sm"
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {logo ? 'Replace logo' : 'Upload logo'}
            </Button>
            {logo && (
              <Button
                variant="ghost"
                size="sm"
                loading={removeMutation.isPending}
                onClick={() => removeMutation.mutate(logo.id)}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function GoogleReviewQrUpload({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: qrAssets } = useQuery({
    queryKey: settingsKeys.locationReviewQr(locationId),
    queryFn: () => mediaApi.list(locationId, 'review_qr'),
    enabled: !!locationId,
  });

  const qrImage = qrAssets?.data?.find((a) => a.status === 'ready') ?? null;

  const removeMutation = useMutation({
    mutationFn: (assetId: string) => mediaApi.remove(locationId, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.locationReviewQr(locationId) });
      toast.success('Google review QR code removed.');
    },
    onError: () => toast.error('Failed to remove QR code.'),
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadFile({ locationId, file, purpose: 'review_qr' });
      queryClient.invalidateQueries({ queryKey: settingsKeys.locationReviewQr(locationId) });
      toast.success('Google review QR code uploaded.');
    } catch {
      toast.error('Upload failed. Use JPG, PNG, or WebP under the size limit.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <Card>
      <div className={styles.assetSection}>
        <div className={styles.assetHeader}>
          <h3 className={styles.assetTitle}>Google Review QR Code</h3>
          <p className={styles.assetDesc}>
            Your practice&apos;s official Google-hosted QR code, downloaded from your Google
            Business Profile. When uploaded, it appears alongside the PN QR code on the printable
            Get Reviews page. JPG, PNG, or WebP.
          </p>
        </div>
        <div className={styles.logoRow}>
          {qrImage ? (
            <img src={qrImage.url} alt="Google review QR code" className={styles.logoPreview} />
          ) : (
            <div className={styles.logoPlaceholder}>No QR code</div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className={styles.hiddenInput}
            onChange={handleFileChange}
          />
          <div className={styles.logoActions}>
            <Button
              variant="ghost"
              size="sm"
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {qrImage ? 'Replace QR code' : 'Upload QR code'}
            </Button>
            {qrImage && (
              <Button
                variant="ghost"
                size="sm"
                loading={removeMutation.isPending}
                onClick={() => removeMutation.mutate(qrImage.id)}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export function AssetsPage() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Assets"
        subtitle="Brand assets for this location — logos and images used across your marketing."
      />
      {locationId ? (
        <>
          <LogoUpload locationId={locationId} />
          <GoogleReviewQrUpload locationId={locationId} />
        </>
      ) : (
        <p className={styles.empty}>No location selected.</p>
      )}
    </div>
  );
}
